export enum BookingEvent {
  CREATED = 'TL_BOOKING_CREATED',
  UPDATED = 'TL_BOOKING_UPDATED',
  CREATE_FAILED = 'TL_BOOKING_CREATE_FAILED',
  CONFIRMED = 'TL_BOOKING_CONFIRMED',
  DATE_UPDATED = 'TL_BOOKING_DATE_UPDATED',
  COMPLETED = 'TL_BOOKING_COMPLETED',
  VOIDED = 'TL_BOOKING_VOIDED',
  CANCELLED = 'TL_BOOKING_CANCELLED',
  REFUNDED = 'TL_BOOKING_REFUNDED',
  FAILED = 'TL_BOOKING_FAILED',
  STATUS_COMPLETE = 'TL_BOOKING_STATUS_COMPLETE',
  STATUS_UPDATED = 'TL_BOOKING_STATUS_UPDATED',
  STATUS_UPDATED_FAILED = 'TL_BOOKING_STATUS_UPDATED_FAILED',
  CHECKED_IN = 'TL_BOOKING_CHECKED_IN',
  // Booking users
  USER_AUTHORIZED = 'TL_BOOKING_USER_AUTHORIZED',
}

export enum BookingType {
  USER = 'USER',
}

export enum BookingStatus {
  OPEN_PENDING = 'OPEN_PENDING', // default state of a booking, should never be left in this state
  // A booking has been started - the user has not commited to anything though
  // - can be updated and modified
  // - no inventory locked
  // - no CC needed
  OPEN = 'OPEN',
  // A booking is in the processes of complete
  // - lock the inventory
  COMPLETE_PENDING = 'COMPLETE_PENDING',
  // A booking is in the stripe checkout process
  // - validate the inventory without locking
  CHECKOUT_PENDING = 'CHECKOUT_PENDING',
  // A booking is complete
  // - inventory reserved
  // - credit card authed
  // - venue gets notified
  // - applied discounts are marked as used
  COMPLETE = 'COMPLETE',
  // A booking has been processed
  // - funds have been submitted for settlement
  // - booking receipt have been sent
  PROCESSED = 'PROCESSED',
  CANCEL_PENDING = 'CANCEL_PENDING',
  CANCELLED = 'CANCELLED',
  VOID_PENDING = 'VOID_PENDING',
  VOIDED = 'VOIDED',
  REFUND_PENDING = 'REFUND_PENDING',
  REFUNDED = 'REFUNDED',
}

export enum BookingStatusName {
  OPEN_PENDING = 'Open Pending',
  OPEN = 'Open',
  COMPLETE_PENDING = 'Pending Complete',
  CHECKOUT_PENDING = 'Pending Checkout',
  COMPLETE = 'Complete',
  PROCESSED = 'Processed',
  CANCEL_PENDING = 'Pending Cancel',
  CANCELLED = 'Canceled',
  VOID_PENDING = 'Pending Void',
  VOIDED = 'Voided',
  REFUND_PENDING = 'Pending Refund',
  REFUNDED = 'Refunded',
}

export enum BookingSort {
  DATE_DESC = '-date',
  DATE_ASC = 'date',
}

export enum CreditType {
  VENUE = 'VENUE',
  GLOBAL = 'GLOBAL',
}

export enum BookingPaymentType {
  ADVANCE = 'ADVANCE', // pay in advance - payment profile required and charged full amount
  ADVANCE_DEPOSIT = 'ADVANCE_DEPOSIT', // pay in advance - payment profile required and charged partial amount
  VENUE = 'VENUE', // pay at the venue - payment profile required and get's authorized
  NONE = 'NONE', // pay in advance - no payment profile required, charged nothing
}

export enum BookingPaymentTypeName {
  ADVANCE = 'Prepaid Full',
  ADVANCE_DEPOSIT = 'Prepaid Deposit',
  VENUE = 'Authorization',
  NONE = 'No payment',
}

export enum BookingUserStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  DECLINED = 'DECLINED',
}

export enum BookingUserPaymentStatus {
  PENDING = 'PENDING', //initial status - user payment profile not charged yet
  CONFIRMED = 'CONFIRMED', //user payment profile charged successfully
  FAILED = 'FAILED', // user payment profile failed when trying to charge
  CANCELLED = 'CANCELLED', // user transaction was cancelled
}

export enum BookingUserType {
  MAIN = 'MAIN',
  SECONDARY = 'SECONDARY',
}

export enum BookingCancellationReason {
  VENUE_CANCELLED = 'VENUE_CANCELLED',
  SOLDOUT = 'SOLDOUT',
  USER = 'USER',
  VOID = 'VOID',
  REFUND = 'REFUND',
  INQUIRY_DECLINED = 'INQUIRY_DECLINED',
  NO_RESPONSE = 'NO_RESPONSE',
  MEMBERSHIP_CANCELLED = 'MEMBERSHIP_CANCELLED',
}

export const allowedCancellationStatuses = [
  BookingStatus.OPEN,
  BookingStatus.OPEN_PENDING,
  BookingStatus.CHECKOUT_PENDING,
  BookingStatus.COMPLETE_PENDING,
]

export const allowedRefundStatuses = [
  BookingStatus.COMPLETE,
  BookingStatus.PROCESSED,
]

export const incompleteBookingStatuses = [
  BookingStatus.OPEN,
  BookingStatus.OPEN_PENDING,
]

export const validBookingStatuses = [
  BookingStatus.COMPLETE,
  BookingStatus.PROCESSED,
]

export const pendingBookingStatuses = [BookingStatus.COMPLETE_PENDING]

export const maxRefundDiffInHours = 48

/** Days after booking date that a user may self-cancel. */
export const bookingCancelDayLimit = 7

export enum BookingJobName {
  UPDATE_CHARGEBACK_EVIDENCE = 'BOOKING_UPDATE_CHARGEBACK_EVIDENCE',
}

export const bookingBasePopulateString = [
  {
    path: 'user',
    select:
      'firstName lastName phone email imageUrl push bookingCount facebook.id membershipLevel gender birthday',
  },
  {
    path: 'users.user',
    select: 'firstName lastName phone email imageUrl push gender birthday',
  },
  {
    path: 'venue',
    select:
      'name location images type reservation showMarketingOptIn marketingOptInText currencyCode currencySymbol languageCode',
  },
  {
    path: 'event',
    select: 'name images',
  },
  {
    path: 'items.inventory',
  },
] as const

export enum BookingErrorMessage {
  CANNOT_REFUND_BOOKING_WITH_PAYMENT_NONE = 'Sorry you cannot refund a booking with payment NONE',
  ITEM_NOT_AVAILABLE = 'Sorry this item is no longer available.',
  CONTACT_TO_CANCEL = 'Please contact us to cancel this order',
  ALREADY_CANCELED = 'Sorry order has already been cancelled',
  INVALID_PAYMENT_TYPE = 'Invalid payment type',
  ONLY_PROMOTER_ALLOWED_TO_MAKE_RESERVATION = 'Only a promoter can make a reservation for someone else.',
  CANNOT_CANCEL_COMPLETE_BOOKING = 'Sorry, you cannot cancel this booking, it has already been completed.',
  NOT_ENABLED_FOR_SALE = 'Sorry this venue is not currently setup to sell.',
  NO_CREDIT = "Sorry you can't apply credit to this type of booking.",
  INVALID_STATUS = 'invalid status for booking type',
  INVENTORY_PURCHASE_MAX = 'Sorry, you can only purchase %s of item %s',
  INVENTORY_PURCHASE_MIN = 'Sorry, you must purchase atleast %s of item %s',
  INVENTORY_PURCHASE_MAX_AGAIN = 'Sorry, you cannot purchase this item again.',
  PRODUCT_SOLD_OUT = 'Sorry, %s is no longer available.',
  PAYMENT_FAILED = 'There was a problem processing your credit card, please try a different card.',
  PAYMENT_PROFILE_REQUIRED = 'You need have a payment method on file to make a purchase.',
  PAYMENT_PROFILE_INVALID = 'A valid payment method must be provided.',
  ONLY_CONFIRMED_CAN_CHARGE = 'Only confirmed users can be charged.',
  USER_ALREADY_CHARGED = 'User was already charged.',
  NO_CONFIRMED_USERS = 'There are no confirmed users on the booking.',
  ALREADY_CHARGED = 'This booking has already been paid for.',
  QUANTITY_TOO_HIGH = 'You cannot purchase that many items.',
  CANNOT_COMPLETE_BOOKING_AGAIN = 'Sorry you cannot complete this booking a second time',
}

export enum BookingDiscountErrorMessage {
  DISCOUNT_EXPIRED = 'Discount is already expired',
  DISCOUNT_USED = 'Discount was already used',
  DISCOUNT_OWNER = 'Discount does not belong to Booking user',
  DISCOUNT_NOT_ON_BOOKING = 'Discount is not applied to Booking',
  DISCOUNT_INVENTORY = 'Discount cannot be applied to this item',
  DISCOUNT_INVENTORY_TYPE = 'Discount cannot be applied to this item',
  DISCOUNT_EVENT = 'Discount cannot be applied for this event',
  COMPLETE_BOOKING_ADD_DISCOUNT = 'Discounts cannot be added to a completed Booking',
  COMPLETE_BOOKING_REMOVE_DISCOUNT = 'Discounts cannot be removed from a completed Booking',
  INVALID_BOOKING_PAYMENT_TYPE = 'Discount cannot be applied for the payment type %s',
  REQUIRES_MAIN_USER = 'Booking requires a main user to add a Discount',
  CREDIT_DISCOUNT = 'Promos for credit are not allowed',
  INVALID_DISCOUNT_TYPE = 'This Discount cannot be used for a booking',
  INVALID_DISCOUNT_AUDIENCE = 'This Discount cannot be used for this audience',
  MEMBERSHIP_DISCOUNT = 'This Discount can only be used for Membership',
  NOT_BOOKING_USER_TYPE = 'This Discount can only be used for bookings created by booking user',
  BOOKING_INVENTORY_TYPE = 'Discounts can only be applied to tables, tickets, or packages',
  AUTHED_USER_COMPLETE_BOOKING = 'You must be logged in to make this purchase',
}
